import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { toast } from "react-toastify";
import { getRcaDashboard } from "../../../../actions/labOpsAnalytics";
import useAbortController from "../../../Common/hooks/useAbortController";
import { formatAntdDateRange } from "../../../../utils/helpers";
import { getDefaultLastOneMonthDateRange, rcaFilterCatalogs, rcaFilterFields } from "../utils/utils";
import {
  buildRcaDashboardParams,
  buildRcaExportFileName,
  buildRcaExportRows,
  buildRcaModalGroupFromBarPoint,
  DEFAULT_RCA_AGGREGATE,
  downloadRcaExportExcel,
  normalizeRcaChartBarSeries,
  normalizeRcaDashboardCount,
  normalizeRcaDashboardGroups,
} from "../utils/rcaUtils";
import DataOpsFilters from "../DataOpsFilters";
import RcaTable from "../tables/RcaTable";
import RcaCharts from "../charts/RcaCharts";
import RcaCategoryModal from "../RcaCategoryModal";
import "../DataOpsAnalytics.css";

const createDefaultRcaFilters = () => ({
  dateRange: getDefaultLastOneMonthDateRange(),
  status: "",
  testCategoryId: [],
  testId: [],
  customerId: "",
  search: "",
  createdBy: "",
  retestReason: "",
  assignTo: "",
});

/**
 * RCA tab — filters + created/completed chart + category table + Excel export.
 * Category row click opens RCA list modal (details live there, not expand-in-table).
 */
export default function RcaTab({ embedded = false }) {
  const isMounted = useRef(true);
  const {
    setNewAbortController,
    clearAbortController,
  } = useAbortController();

  const [filters, setFilters] = useState(createDefaultRcaFilters);
  const [groups, setGroups] = useState([]);
  const [chartBarSeries, setChartBarSeries] = useState({
    customer: [], test: [], assignee: [],
  });
  const [summaryCount, setSummaryCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [timelineAggregate, setTimelineAggregate] = useState(DEFAULT_RCA_AGGREGATE);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setSelectedGroup(null);
  }, [filters]);

  useEffect(() => {
    const fetchRcaGroups = async () => {
      const params = buildRcaDashboardParams(filters, formatAntdDateRange);
      if (!params) {
        setGroups([]);
        setChartBarSeries({ customer: [], test: [], assignee: [] });
        setSummaryCount(null);
        return;
      }

      setLoading(true);
      const abortController = setNewAbortController();
      const response = await getRcaDashboard(params, abortController.signal);

      if (!isMounted.current) {
        return;
      }

      if (response.canceled) {
        setLoading(false);
        clearAbortController();
        return;
      }

      if (response.success) {
        setGroups(normalizeRcaDashboardGroups(response.result));
        setSummaryCount(normalizeRcaDashboardCount(response.count));

        const barSeries = normalizeRcaChartBarSeries(response.chart_breakdown);
        if (!barSeries) {
          setChartBarSeries({ customer: [], test: [], assignee: [] });
          toast.error("Chart breakdown data is unavailable.");
        } else {
          setChartBarSeries(barSeries);
        }
      } else {
        setGroups([]);
        setChartBarSeries({ customer: [], test: [], assignee: [] });
        setSummaryCount(null);
        toast.error(response.message || "Failed to load RCA dashboard.");
      }

      setLoading(false);
      clearAbortController();
    };

    fetchRcaGroups();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRcaBarDrillDown = useCallback((point) => {
    if (!point?.drillKind) {
      return;
    }
    const modalGroup = buildRcaModalGroupFromBarPoint(point, groups);
    if (!modalGroup) {
      toast.warn("No RCA details for this selection.");
      return;
    }
    setSelectedGroup(modalGroup);
  }, [groups]);

  const handleExportTableData = () => {
    if (exportLoading) {
      return;
    }

    setExportLoading(true);
    try {
      const exportRows = buildRcaExportRows(groups);
      if (!exportRows.length) {
        toast.warn("No data available to export for the current filters.");
        return;
      }
      downloadRcaExportExcel(exportRows, buildRcaExportFileName());
      toast.success("RCA data exported successfully.");
    } catch (error) {
      toast.error("Failed to export RCA data.");
    } finally {
      setExportLoading(false);
    }
  };

  const content = (
    <>
      <DataOpsFilters
        idPrefix="rca"
        fields={rcaFilterFields}
        catalogs={rcaFilterCatalogs}
        filters={filters}
        onFiltersChange={setFilters}
        onExport={handleExportTableData}
        exportLoading={exportLoading}
        exportDisabled={loading || groups.length === 0}
      />

      <div className="DataOpsAnalytics__StackedLayout">
        <div className="DataOpsAnalytics__ChartsPane">
          <RcaCharts
            groups={groups}
            chartBarSeries={chartBarSeries}
            loading={loading}
            timelineAggregate={timelineAggregate}
            onTimelineAggregateChange={setTimelineAggregate}
            onBarDrillDown={handleRcaBarDrillDown}
          />
        </div>
        <div className="DataOpsAnalytics__TablePane DataOpsAnalytics__TablePane--wide">
          <RcaTable
            groups={groups}
            loading={loading}
            summaryCount={summaryCount}
            onViewGroup={setSelectedGroup}
          />
        </div>
      </div>

      <RcaCategoryModal
        open={Boolean(selectedGroup)}
        group={selectedGroup}
        onClose={() => setSelectedGroup(null)}
      />
    </>
  );

  if (embedded) {
    return content;
  }

  return <div className="DataOpsAnalytics">{content}</div>;
}
